/**
 * Pipeline Connector Wizard — one-click Airflow / Dagster scan.
 *
 * Lives on /admin/pipeline-dedup. Operator picks platform → enters URL + creds →
 * we probe → save encrypted credentials → "Scan now" pulls live inventory and
 * runs the dedup algorithm. Replaces the paste-JSON flow with a connect-once
 * paradigm.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Plug, CheckCircle2, AlertTriangle, Trash2, Play, KeyRound, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const initialAirflow = { base_url: '', auth_kind: 'basic', username: '', password: '', bearer_token: '' };
const initialDagster = { graphql_url: '', cloud_token: '', bearer_token: '' };

function ProbeBadge({ state }) {
  if (state === 'idle') return null;
  if (state === 'testing') return <Badge className="bg-slate-700/60 text-slate-200">Testing…</Badge>;
  if (state === 'ok') return <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"><CheckCircle2 className="w-3 h-3 inline mr-1" /> Connected</Badge>;
  return <Badge className="bg-rose-500/15 text-rose-300 border border-rose-500/30"><AlertTriangle className="w-3 h-3 inline mr-1" /> Failed</Badge>;
}

export default function PipelineConnectorWizard({ onScanResult }) {
  const [connectors, setConnectors] = useState([]);
  const [airflow, setAirflow] = useState(initialAirflow);
  const [dagster, setDagster] = useState(initialDagster);
  const [airflowProbe, setAirflowProbe] = useState({ state: 'idle', msg: null });
  const [dagsterProbe, setDagsterProbe] = useState({ state: 'idle', msg: null });
  const [scanBusy, setScanBusy] = useState(null);
  const [scanError, setScanError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/pipeline-connectors/connectors`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setConnectors(d.connectors || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const callApi = async (path, method, body) => {
    const token = localStorage.getItem('token');
    const r = await fetch(`${API}${path}`, {
      method, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const text = await r.text();
    let json = null;
    try { json = JSON.parse(text); } catch { /* non-json */ }
    return { ok: r.ok, status: r.status, body: json || text };
  };

  const testAirflow = async () => {
    setAirflowProbe({ state: 'testing', msg: null });
    const r = await callApi('/api/pipeline-connectors/airflow/test', 'POST', airflow);
    if (r.ok && r.body.ok) setAirflowProbe({ state: 'ok', msg: `Airflow ${r.body.airflow_version || 'OK'}` });
    else setAirflowProbe({ state: 'fail', msg: r.body?.error || r.body?.detail || `HTTP ${r.status}` });
  };

  const connectAirflow = async () => {
    setAirflowProbe({ state: 'testing', msg: null });
    const r = await callApi('/api/pipeline-connectors/airflow/connect', 'POST', airflow);
    if (r.ok) {
      setAirflowProbe({ state: 'ok', msg: `Saved · Airflow ${r.body.airflow_version || ''}`.trim() });
      setAirflow(initialAirflow);
      refresh();
    } else setAirflowProbe({ state: 'fail', msg: r.body?.detail || `HTTP ${r.status}` });
  };

  const testDagster = async () => {
    setDagsterProbe({ state: 'testing', msg: null });
    const r = await callApi('/api/pipeline-connectors/dagster/test', 'POST', dagster);
    if (r.ok && r.body.ok) setDagsterProbe({ state: 'ok', msg: 'Dagster reachable' });
    else setDagsterProbe({ state: 'fail', msg: r.body?.error || r.body?.detail || `HTTP ${r.status}` });
  };

  const connectDagster = async () => {
    setDagsterProbe({ state: 'testing', msg: null });
    const r = await callApi('/api/pipeline-connectors/dagster/connect', 'POST', dagster);
    if (r.ok) {
      setDagsterProbe({ state: 'ok', msg: 'Saved' });
      setDagster(initialDagster);
      refresh();
    } else setDagsterProbe({ state: 'fail', msg: r.body?.detail || `HTTP ${r.status}` });
  };

  const scan = async (platform) => {
    setScanBusy(platform);
    setScanError(null);
    const r = await callApi(`/api/pipeline-connectors/${platform}/scan`, 'POST', { self_hosted: false });
    setScanBusy(null);
    if (r.ok) {
      onScanResult?.(r.body);
    } else {
      setScanError(`${platform}: ${r.body?.detail || `HTTP ${r.status}`}`);
    }
  };

  const removeConnector = async (platform) => {
    if (!window.confirm(`Remove ${platform} connector? Encrypted credentials will be deleted.`)) return;
    await callApi(`/api/pipeline-connectors/connectors/${platform}`, 'DELETE');
    refresh();
  };

  const hasAirflow = connectors.find((c) => c.platform === 'airflow');
  const hasDagster = connectors.find((c) => c.platform === 'dagster');

  return (
    <Card className="bg-slate-900 border-emerald-500/30" data-testid="pipeline-connector-wizard">
      <CardHeader>
        <CardTitle className="text-base text-white flex items-center gap-2">
          <Plug className="w-4 h-4 text-emerald-400" />
          Connect Airflow / Dagster (1-click scan)
        </CardTitle>
        <p className="text-xs text-slate-200 mt-1">
          Skip the paste-JSON step. Connect once with read-only API credentials (encrypted at rest with AES-128) → click "Scan now" → see dedup findings in seconds.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Saved connectors */}
        {connectors.length > 0 && (
          <div className="rounded-md bg-emerald-500/5 border border-emerald-500/20 p-3" data-testid="saved-connectors">
            <div className="text-[10px] font-bold text-emerald-300 uppercase tracking-widest mb-2 flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> Saved connectors</div>
            {connectors.map((c) => (
              <div key={c.platform} className="flex items-center justify-between text-xs py-1.5">
                <div className="flex items-center gap-3">
                  <span className="font-bold text-white uppercase">{c.platform}</span>
                  <span className="font-mono text-slate-400">{c.base_url || c.graphql_url}</span>
                  {c.username_preview && <span className="text-slate-500">· user: <span className="font-mono">{c.username_preview}</span></span>}
                  {c.token_kind && <span className="text-slate-500">· auth: {c.token_kind}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={() => scan(c.platform)} disabled={scanBusy === c.platform}
                            className="bg-emerald-600 hover:bg-emerald-500 text-white h-7 text-xs"
                            data-testid={`scan-${c.platform}-btn`}>
                    {scanBusy === c.platform ? 'Scanning…' : <><Play className="w-3 h-3 mr-1" /> Scan now</>}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => removeConnector(c.platform)} className="text-rose-400 hover:text-rose-300 h-7 px-2">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
            {scanError && <div className="mt-2 text-xs text-rose-300">{scanError}</div>}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Airflow form */}
          {!hasAirflow && (
            <div className="rounded-md bg-slate-950 border border-slate-700 p-3" data-testid="airflow-form">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold text-white flex items-center gap-1.5"><KeyRound className="w-3 h-3 text-cyan-300" /> Airflow REST</div>
                <ProbeBadge state={airflowProbe.state} />
              </div>
              <input type="text" placeholder="https://airflow.example.com" value={airflow.base_url}
                       onChange={(e) => setAirflow({ ...airflow, base_url: e.target.value })}
                       className="w-full mb-2 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white"
                       data-testid="airflow-url-input" />
              <select value={airflow.auth_kind} onChange={(e) => setAirflow({ ...airflow, auth_kind: e.target.value })}
                        className="w-full mb-2 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white">
                <option value="basic">Basic auth (default Airflow)</option>
                <option value="bearer">Bearer token (MWAA / managed)</option>
              </select>
              {airflow.auth_kind === 'basic' ? (
                <>
                  <input type="text" placeholder="username" value={airflow.username}
                           onChange={(e) => setAirflow({ ...airflow, username: e.target.value })}
                           className="w-full mb-2 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white" />
                  <input type="password" placeholder="password" value={airflow.password}
                           onChange={(e) => setAirflow({ ...airflow, password: e.target.value })}
                           className="w-full mb-2 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white" />
                </>
              ) : (
                <input type="password" placeholder="bearer token" value={airflow.bearer_token}
                         onChange={(e) => setAirflow({ ...airflow, bearer_token: e.target.value })}
                         className="w-full mb-2 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white" />
              )}
              <div className="flex gap-2 mt-1">
                <Button size="sm" variant="outline" onClick={testAirflow} className="text-xs h-7" data-testid="test-airflow-btn">Test</Button>
                <Button size="sm" onClick={connectAirflow} className="bg-cyan-600 hover:bg-cyan-500 text-white text-xs h-7" data-testid="connect-airflow-btn">Save</Button>
              </div>
              {airflowProbe.msg && (
                <div className={`mt-2 text-xs ${airflowProbe.state === 'ok' ? 'text-emerald-300' : 'text-rose-300'}`}>{airflowProbe.msg}</div>
              )}
            </div>
          )}

          {/* Dagster form */}
          {!hasDagster && (
            <div className="rounded-md bg-slate-950 border border-slate-700 p-3" data-testid="dagster-form">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold text-white flex items-center gap-1.5"><KeyRound className="w-3 h-3 text-violet-300" /> Dagster GraphQL</div>
                <ProbeBadge state={dagsterProbe.state} />
              </div>
              <input type="text" placeholder="https://dagster.example.com/graphql" value={dagster.graphql_url}
                       onChange={(e) => setDagster({ ...dagster, graphql_url: e.target.value })}
                       className="w-full mb-2 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white"
                       data-testid="dagster-url-input" />
              <input type="password" placeholder="Dagster Cloud token (or leave blank)" value={dagster.cloud_token}
                       onChange={(e) => setDagster({ ...dagster, cloud_token: e.target.value })}
                       className="w-full mb-2 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white" />
              <input type="password" placeholder="OR Bearer token (self-hosted)" value={dagster.bearer_token}
                       onChange={(e) => setDagster({ ...dagster, bearer_token: e.target.value })}
                       className="w-full mb-2 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white" />
              <div className="flex gap-2 mt-1">
                <Button size="sm" variant="outline" onClick={testDagster} className="text-xs h-7" data-testid="test-dagster-btn">Test</Button>
                <Button size="sm" onClick={connectDagster} className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-7" data-testid="connect-dagster-btn">Save</Button>
              </div>
              {dagsterProbe.msg && (
                <div className={`mt-2 text-xs ${dagsterProbe.state === 'ok' ? 'text-emerald-300' : 'text-rose-300'}`}>{dagsterProbe.msg}</div>
              )}
            </div>
          )}
        </div>

        <div className="text-[10px] text-slate-500 italic flex items-start gap-1">
          <ShieldCheck className="w-3 h-3 mt-0.5 shrink-0 text-emerald-400/70" />
          Credentials are encrypted with the same Fernet vault that protects your cloud-provider creds. Test endpoint probes without saving; only "Save" persists.
        </div>
      </CardContent>
    </Card>
  );
}
